import React, { forwardRef, MouseEventHandler } from "react"
import { tv } from "tailwind-variants"
import { type VariantProps } from "tailwind-variants"
import { disabledVariants } from "../../styles"
import { classNames } from "../../utils"
import {
  isClickableElement,
  isInteractiveElement,
  isLinkElement,
} from "../../utils/element"
import { Avatar, AvatarElement, AvatarProps } from "../Avatar"
import { FlexProps } from "../Flex"
import { FlexColumn, FlexColumnProps } from "../FlexColumn"
import { Link, LinkProps, Url } from "../Link"
import { Slot } from "../Slot"
import { Text, TextBodyProps } from "../Text"
import { UnstyledButton } from "../UnstyledButton"
import { VerticalAligned, VerticalAlignedProps } from "../VerticalAligned"

export type ItemRenderLinkComponent = React.ComponentType<
  Omit<LinkItemProps, "renderLink">
>

export type ItemPropsBase = FlexProps &
  VariantProps<typeof itemVariants> & {
    disabled?: boolean
    onClick?: MouseEventHandler<HTMLElement>
    renderLink?: ItemRenderLinkComponent
  }

const itemVariants = tv({
  base: "flex w-full items-center gap-3 border border-border-2 p-4",
  variants: {
    active: {
      true: "bg-bg-additional-2",
    },
    interactive: {
      true: "cursor-pointer hover:bg-bg-additional-1 active:bg-bg-additional-2",
    },
    disabled: {
      true: disabledVariants({ disabled: true }),
    },
    variant: {
      unstyled: "border-0 p-0",
      vertical: "flex-col items-start border-0 p-0",
    },
  },
})

type LinkItemProps = ItemPropsBase & {
  href: Url
} & LinkProps

export type DefaultItemProps = ItemPropsBase & {
  href?: undefined
  target?: string
}

export type ItemProps = DefaultItemProps | LinkItemProps

const LinkItem = forwardRef<HTMLAnchorElement, LinkItemProps>(function LinkItem(
  { renderLink, ...rest },
  ref,
) {
  const Comp = renderLink ?? Link

  return <Comp ref={ref} variant="unstyled" {...rest} />
})

const ItemBase = forwardRef<HTMLElement, ItemProps>(function Item(
  { asChild, children, variant, interactive, renderLink, ...props },
  ref,
) {
  const isInteractive = interactive ?? isInteractiveElement(props)

  const { className, disabled, href, target, onClick, ...rest } = props

  // onClick is added to contentProps so that it can be used in the
  // is{Link,Clickable}Element functions but it will be applied to the
  // outer Slot element because it is a valid prop for any element.
  const contentProps = { href, target, onClick, disabled }

  const renderContent = () => {
    if (asChild) {
      return children
    }

    if (isLinkElement(contentProps)) {
      return (
        <LinkItem
          disabled={contentProps.disabled}
          href={contentProps.href}
          renderLink={renderLink}
          target={contentProps.target}
        >
          {children}
        </LinkItem>
      )
    }

    if (isClickableElement(contentProps)) {
      return (
        <UnstyledButton disabled={contentProps.disabled}>
          {children}
        </UnstyledButton>
      )
    }

    return <div>{children}</div>
  }

  const isUnstyled = variant === "unstyled"

  return (
    <Slot
      className={classNames(
        itemVariants({
          active: !isUnstyled && rest.active,
          interactive: !isUnstyled && !disabled && isInteractive,
          disabled: contentProps.disabled,
          variant,
        }),
        className,
      )}
      data-id="Item"
      ref={ref}
      onClick={onClick}
      {...rest}
    >
      {renderContent()}
    </Slot>
  )
})

export const ItemAvatar = forwardRef<AvatarElement, AvatarProps>(
  function ItemAvatar({ className, ...rest }, ref) {
    return (
      <Avatar
        className={classNames("order-1 shrink-0 text-text-primary", className)}
        data-id="ItemAvatar"
        ref={ref}
        {...rest}
      />
    )
  },
)

export const ItemContent = ({ className, ...rest }: VerticalAlignedProps) => {
  return (
    <VerticalAligned
      className={classNames(
        "order-2 overflow-hidden",
        "flex-auto items-start self-stretch",
        className,
      )}
      data-id="ItemContent"
      {...rest}
    />
  )
}

export const ItemTitle = forwardRef<HTMLSpanElement, TextBodyProps>(
  function ItemTitle(props, ref) {
    return (
      <Text
        color="text-primary"
        data-id="ItemTitle"
        ref={ref}
        weight="semibold"
        {...props}
      />
    )
  },
)

export const ItemDescription = forwardRef<HTMLSpanElement, TextBodyProps>(
  function ItemDescription(props, ref) {
    return (
      <Text
        color="text-secondary"
        data-id="ItemDescription"
        ref={ref}
        {...props}
      />
    )
  },
)

export const ItemSide = ({ className, ...rest }: FlexColumnProps) => {
  return (
    <FlexColumn
      className={classNames(
        "order-3 max-w-[40%] overflow-hidden text-right",
        "flex-none justify-center self-stretch",
        className,
      )}
      {...rest}
    />
  )
}

export const ItemAction = ({ className, ...rest }: FlexColumnProps) => {
  return (
    <FlexColumn
      className={classNames("order-4 ml-3 justify-center", className)}
      {...rest}
    />
  )
}

/**
 * A common component for rendering a grouping of an Avatar with content such as Title and Description.
 * Can be used in a variety of places such as a list of users, activity rows, etc.
 */
export const Item = Object.assign(ItemBase, {
  Avatar: Object.assign(ItemAvatar, { Badge: Avatar.Badge }),
  Content: ItemContent,
  Title: ItemTitle,
  Description: ItemDescription,
  Side: ItemSide,
  Action: ItemAction,
})
